import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { AuthService } from '../../../services/auth.service';
import { PaymentsService } from '../../../services/payments.service';
import { LabelService } from '../../../services/label.service';
import { ConfirmationPanelComponent } from '../../../shared/confirmation-panel/confirmation-panel.component';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DoctorService } from '../../../services/doctor.service';
import { ErrorPanelService } from '../../../services/error-panel.service';
import { SharedModule } from '../../../shared/shared.module';
import { FiscalCodePanelComponent } from '../../../shared/fiscal-code-panel/fiscal-code-panel.component';
import { DoctorFiscalIdRequestModel } from '../../../models/api/billing-data.api.model';
import {
  ClineaseRole,
  ClineaseRoleResponse,
} from '../../../models/doctor-model';
import { CustomerPortalResponse } from '../../../models/payments-model';

@Component({
  selector: 'app-subscription-renewal',
  standalone: true,
  templateUrl: './subscription-renewal.component.html',
  styleUrls: ['./subscription-renewal.component.scss'],
  imports: [MatIconModule, MatButtonModule, SharedModule],
})
export class SubscriptionRenewalComponent {
  benefits = [
    'Continua a utilizzare tutte le funzionalità di PsicoGenius',
    'Accesso alle nuove funzionalità AI',
    'Supporto',
    'Analisi delle sessioni',
  ];
  isDeletingAccount: boolean = false;
  fiscalCode: any;

  constructor(
    private http: HttpClient,
    private paymentsService: PaymentsService,
    private doctorService: DoctorService,
    public labelService: LabelService,
    public dialog: MatDialog,
    private router: Router,
    public errorPanelService: ErrorPanelService
  ) {}

  renewSubscription() {
    // 2 different flows: one for users that actually need to
    // renew their subscription (1) and one for admins (2)
    // 1: call create customer portal session and make sure
    //     that te renew is correctly associated to the existing
    //     stripe customer and subscription
    // 2: call create checkout session because such users have no stripe customer + subscription
    //     associated to them.

    // TODO: we could remove the fiscal code check if all doctors had the `codice_fiscale` field set. Unfortunately right now there are some users without `codice_fiscale`. Some of them are admins, some of them are not.
    this.doctorService.getDoctorFiscalCode().subscribe({
      next: (response: DoctorFiscalIdRequestModel) => {
        this.fiscalCode = response.codice_fiscale;
        // we should almost never enter here because all new doctors should have the fiscal code
        if (!this.fiscalCode) {
          const dialogRef = this.dialog.open(FiscalCodePanelComponent);

          dialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
              this.paymentsService.createCheckoutSession().subscribe({
                next: (response: any) => {
                  window.location.href = response.checkout_session_url;
                },
                error: (error: any) => {
                  console.error('Error creating checkout session:', error);
                },
              });
            }
          });
        } else {
          // we should almost always enter here because all new doctors should have the fiscal code
          this.doctorService.getDoctorRole().subscribe({
            next: (response: ClineaseRoleResponse) => {
              // let's first check if the doctor has no stripe customer + subscription associated to him
              switch (response.role) {
                case ClineaseRole.EXISTING_DOCTOR_WITHOUT_STRIPE_ACCOUNT.valueOf():
                case ClineaseRole.EXISTING_DOCTOR_WITHOUT_STRIPE_SUBSCRIPTION.valueOf():
                  this.paymentsService.createCheckoutSession().subscribe({
                    next: (response: any) => {
                      window.location.href = response.checkout_session_url;
                    },
                    error: (error: any) => {
                      console.error('Error creating checkout session:', error);
                      this.errorPanelService.showErrorSnackBar(
                        'Errore nel creare la sessione di gestione della sottoscrizione'
                      );
                    },
                  });
                  break;
                // if the doctor is a non payer, we create a customer portal session to allow him to manage his subscription
                case ClineaseRole.DOCTOR_NON_PAYER.valueOf():
                  this.paymentsService.createCustomerPortalSession().subscribe({
                    next: (response: CustomerPortalResponse) => {
                      window.open(response.portal_url, '_blank');
                    },
                    error: (error: any) => {
                      console.error(
                        'Error creating customer portal session:',
                        error
                      );
                      this.errorPanelService.showErrorSnackBar(
                        'Errore nel creare la sessione di gestione della sottoscrizione'
                      );
                    },
                  });
                  break;
                // if the doctor is a payer, we do nothing
                case ClineaseRole.DOCTOR_PAYER.valueOf():
                  break;
                default:
                  break;
              }
            },
            error: (error: any) => {
              console.error('Error fetching doctor role:', error);
              this.errorPanelService.showErrorSnackBar(
                "Errore nel recuperare le informazioni dell'utente"
              );
            },
          });
        }
      },
      error: (error: any) => {
        console.error('Error fetching fiscal ID:', error);
      },
    });
  }

  onDeleteClick() {
    const dialogRef = this.dialog.open(ConfirmationPanelComponent, {
      data: {
        title: 'Sei sicuro di voler eliminare il tuo account?',
        infoText: `Verranno immediatamente cancellati tutti i tuoi dati e quelli relativi ai tuoi pazienti, inclusi tutti i documenti e risultati di pipeline AI.
          Questa azione è irreversibile. Eliminando il tuo account non potrai più richiedere una copia dei tuoi dati.`,
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.isDeletingAccount = true;
        this.doctorService.deleteDoctor().subscribe({
          next: () => {
            this.errorPanelService.showInfoSnackBar(
              'Il tuo account, e tutti i dati associati, sono stati eliminati con successo'
            );
            this.router.navigate(['/']);
            this.isDeletingAccount = false;
          },
          error: (error) => {
            console.error("Errore durante l'eliminazione del profilo:", error);
            this.errorPanelService.showErrorSnackBar(
              "Errore durante l'eliminazione del profilo"
            );
            this.isDeletingAccount = false;
          },
        });
      }
    });
  }
}
